// Single, hardened rasterization engine for every download surface.
//
// WHY THIS EXISTS
// ---------------
// The app used to raster DOM in three separate places with three slightly
// different dom-to-image invocations:
//   * the editor (UploadFloor.downloadLayout — live plan capture),
//   * the gallery (LayoutVersionSlider.downloadGalleryImage — saved image
//     wrapped in a logo/disclaimer frame),
//   * the branded flyer (BrandedExportModal.handleDownload).
//
// Each relied on a single dom-to-image "warm-up" pass to settle late-decoding
// <img> rasters before the real capture. That warm-up is NOT a guarantee: under
// a heavy clone (e.g. after shuffle + adding items, when many furniture rasters
// plus the large base-plan raster all need decoding), the warm-up could return
// before every image was actually decoded, and the real capture then shipped a
// PARTIAL image — most visibly the base floor plan missing while furniture
// rendered. The only downstream guard was a ~5 KB blob-size check, which a
// furniture-only image clears easily, so the broken export shipped silently.
//
// This module makes every surface raster through ONE path with a deterministic
// contract:
//   1. decodeAllImages(node) — await img.decode() on EVERY <img> in the subtree
//      so nothing is captured half-decoded.
//   2. rasterizeNode(node)   — one warm-up pass + the real capture, with the
//      shared font/CORS filters applied every time.
//
// Callers that require structural correctness (the editor plan capture) verify
// their critical nodes decoded (see decodeAllImages' return counts / their own
// naturalWidth checks) and refuse to emit a partial image.

import domtoimage from "dom-to-image-more";

const DEFAULT_DECODE_TIMEOUT_MS = 8000;

// External Google Fonts stylesheets have no bearing on the raster and only add
// a CORS failure surface, so every capture strips them identically.
export const captureDomToImageFilter = (node) => {
  if (node.nodeName === "LINK" && node.rel === "stylesheet") {
    const href = node.href || "";
    if (href.includes("googleapis.com") || href.includes("gstatic.com")) {
      return false;
    }
  }
  if (node.nodeName === "STYLE" && node.innerHTML) {
    if (
      node.innerHTML.includes("@import") &&
      (node.innerHTML.includes("googleapis.com") ||
        node.innerHTML.includes("gstatic.com"))
    ) {
      return false;
    }
  }
  return true;
};

export const captureCssFetch = (url) => {
  if (
    url.includes("googleapis.com") ||
    url.includes("gstatic.com") ||
    url.includes("fonts.")
  ) {
    return Promise.resolve("");
  }
  return fetch(url)
    .then((response) => response.text())
    .catch(() => "");
};

/**
 * Force every <img> inside `node` to fully decode before capture.
 *
 * This is the deterministic replacement for relying on dom-to-image's warm-up
 * pass to settle late-decoding rasters (the root cause of partial captures —
 * e.g. the base floor plan missing while furniture rendered).
 *
 * Never throws: a single image that fails to decode is counted, not fatal, so
 * callers decide (via the returned counts or their own node checks) whether the
 * capture is acceptable.
 *
 * @param {Element} node
 * @param {{ timeoutMs?: number }} [opts]
 * @returns {Promise<{ total: number, decoded: number, failed: number }>}
 */
export async function decodeAllImages(node, opts = {}) {
  const timeoutMs = opts.timeoutMs ?? DEFAULT_DECODE_TIMEOUT_MS;
  if (!node || typeof node.querySelectorAll !== "function") {
    return { total: 0, decoded: 0, failed: 0 };
  }

  const imgs = Array.from(node.querySelectorAll("img"));
  let decoded = 0;
  let failed = 0;

  const withTimeout = (promise) =>
    Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error("decode-timeout")), timeoutMs)
      ),
    ]);

  await Promise.all(
    imgs.map(async (img) => {
      try {
        const src = img.getAttribute("src");
        if (!src) {
          failed += 1;
          return;
        }
        if (typeof img.decode === "function") {
          await withTimeout(img.decode());
        } else if (!(img.complete && img.naturalWidth > 0)) {
          await withTimeout(
            new Promise((resolve, reject) => {
              img.addEventListener("load", resolve, { once: true });
              img.addEventListener("error", reject, { once: true });
            })
          );
        }
        if (img.naturalWidth > 0) decoded += 1;
        else failed += 1;
      } catch {
        // decode() can reject (EncodingError, timeout) even for a node that
        // would still paint; fall back to the completeness heuristic.
        if (img.complete && img.naturalWidth > 0) decoded += 1;
        else failed += 1;
      }
    })
  );

  return { total: imgs.length, decoded, failed };
}

/**
 * Raster a DOM node to an image via dom-to-image, applying the shared font/CORS
 * filters and the warm-up discipline every time.
 *
 * IMPORTANT: call decodeAllImages(node) first. The warm-up toJpeg pass below is
 * retained (it forces dom-to-image's internal clone → inline → foreignObject
 * render to fully settle once), but it is a belt-and-suspenders companion to
 * decodeAllImages, not a substitute for it.
 *
 * @param {Element} node
 * @param {Object} [options] - dom-to-image options (width, height, style, …)
 *   plus `as`: "blob" (default) | "jpeg" | "png".
 * @returns {Promise<Blob|string>} Blob for "blob", data URL for "jpeg"/"png".
 */
export async function rasterizeNode(node, options = {}) {
  const { as = "blob", filter, cssFetch, ...rest } = options;

  const domOptions = {
    quality: 1,
    skipFonts: true,
    cacheBust: false,
    imagePlaceholder: undefined,
    filter: filter || captureDomToImageFilter,
    cssFetch: cssFetch || captureCssFetch,
    ...rest,
  };

  // Warm-up pass — see the "DO NOT DELETE" history: dom-to-image's pipeline
  // (clone → inline CSS → resolve <img> → decode → render) runs once per call.
  // The first (discarded) call settles the cloned subtree so the real capture
  // below produces a complete raster.
  await domtoimage.toJpeg(node, domOptions);

  if (as === "png") return domtoimage.toPng(node, domOptions);
  if (as === "jpeg") return domtoimage.toJpeg(node, domOptions);
  return domtoimage.toBlob(node, domOptions);
}
